import { isAxiosError } from 'axios';
import { api, createRequestId } from './client';

const RETRY_WINDOW_MS = 10 * 60 * 1000;
const pendingRequestIds = new Map<string, { requestId: string; createdAt: number }>();

function mutationKey(method: string, url: string, body: unknown): string {
  return `${method}:${url}:${JSON.stringify(body)}`;
}

interface AdminMutationOptions {
  keepRequestIdForStatuses?: number[];
}

interface AdminMutationResponse<T> {
  data: T;
  status: number;
}

async function adminMutation<T>(
  method: 'post' | 'delete',
  url: string,
  body: unknown,
  options: AdminMutationOptions = {},
): Promise<AdminMutationResponse<T>> {
  const key = mutationKey(method, url, body);
  const existing = pendingRequestIds.get(key);
  const now = Date.now();
  const reusable = existing && now - existing.createdAt < RETRY_WINDOW_MS ? existing : null;
  const requestId = reusable?.requestId ?? createRequestId();

  pendingRequestIds.set(key, { requestId, createdAt: reusable?.createdAt ?? now });
  try {
    const headers = { 'X-Request-ID': requestId };
    const response = method === 'post'
      ? await api.post<T>(url, body, { headers })
      : await api.delete<T>(url, { data: body, headers });
    if (!options.keepRequestIdForStatuses?.includes(response.status)) {
      pendingRequestIds.delete(key);
    }
    return { data: response.data, status: response.status };
  } catch (error) {
    const keepRequestId = isAxiosError(error)
      && error.response
      && options.keepRequestIdForStatuses?.includes(error.response.status);
    if (!keepRequestId && (!isAxiosError(error) || error.response)) {
      pendingRequestIds.delete(key);
    }
    throw error;
  }
}

export function postAdminMutation<T>(url: string, body: unknown): Promise<T> {
  return adminMutation<T>('post', url, body).then((response) => response.data);
}

export function deleteAdminMutationWithStatus<T>(url: string, body: unknown): Promise<AdminMutationResponse<T>> {
  return adminMutation<T>('delete', url, body, { keepRequestIdForStatuses: [202, 503] });
}
